// WS5 PR3.1 — multi-viewbox state primitives.
//
// Layer puro: tipi + costanti. Reducer in `state-machine.ts`, pure math
// (clamp AABB) in `aabb.ts`, orchestrator wiring in
// `src/orchestrator/orchestrator.ts` (PR3.2).
//
// Cfr. docs/PLAN_WS5_first_person_viewbox.md §8 + PR1 backend types
// (src/api/types.ts ModelViewbox) per il payload manifest.

import type { ModelViewbox, Vec3 } from "../api/types.js";

/** Padding minimo (m) tra eye e parete headbox AABB. EYE_HEIGHT=1.65 in
 *  walk/types — il clamp NON deve forzare la testa contro il soffitto;
 *  riserviamo questo padding sopra/sotto/lati. Valore conservativo basato
 *  su misure tipiche (~10 cm dal muro perceptual comfort). */
export const HEADBOX_EDGE_PADDING_M = 0.1;

/** Durata teleport tra viewbox (ms). Desktop 800ms = UX matterport-grade.
 *  Mobile e' impostato a 600ms nell'orchestrator per percezione fluida
 *  (vedi PLAN §8.4). reduced-motion → 0ms (override client-side). */
export const TELEPORT_GLIDE_MS = 800;
export const TELEPORT_GLIDE_MS_MOBILE = 600;

/** Fase corrente di un teleport viewbox-to-viewbox. */
export type TeleportPhase = "idle" | "gliding";

/** State slice dedicato a multi-viewbox awareness. Owned dall'orchestrator,
 *  consumato da `walkTick` (AABB clamp), UI switcher, cinematic tour. */
export interface ViewboxState {
	/** Lista delle viewbox per la scena corrente. Vuota = scena legacy
	 *  (fallback orbit, FP disabilitato). */
	available: readonly ModelViewbox[];
	/** Index nella lista `available` della viewbox attualmente attiva (NON
	 *  e' il `viewbox.index` numerico, che puo' essere non contiguo).
	 *  null = nessuna viewbox attiva (es. scena legacy o pre-spawn). */
	currentArrayIdx: number | null;
	/** Stato corrente del teleport (gliding tra viewbox → walk/click-to-move
	 *  bloccati lato orchestrator per evitare race coordinate). */
	teleportPhase: TeleportPhase;
	/** Se true, il viewer ha viewbox.length>=1 e first-person e' la modalita'
	 *  default a load. False per scene legacy → orbit fallback. */
	firstPersonAvailable: boolean;
}

/** Snapshot emesso ai listener (HUD, UI switcher) al cambio di state. */
export interface ViewboxSnapshot {
	available: readonly ModelViewbox[];
	currentArrayIdx: number | null;
	teleportPhase: TeleportPhase;
	firstPersonAvailable: boolean;
}

/** Eventi reducer (cfr. state-machine.ts). */
export type ViewboxEvent =
	| { type: "LOAD"; viewboxes: readonly ModelViewbox[]; defaultIdx: number | null }
	| { type: "SELECT"; arrayIdx: number }
	| { type: "TELEPORT_START"; arrayIdx: number }
	| { type: "TELEPORT_COMPLETE" }
	| { type: "TELEPORT_ABORT" }
	| { type: "RESET" };

/** Stato iniziale (pre-load scena). */
export const INITIAL_VIEWBOX_STATE: ViewboxState = {
	available: [],
	currentArrayIdx: null,
	teleportPhase: "idle",
	firstPersonAvailable: false,
};

/** Helper: ritorna la viewbox attiva, o null se nessuna o array-idx fuori range. */
export function currentViewbox(state: ViewboxState): ModelViewbox | null {
	if (state.currentArrayIdx === null) return null;
	return state.available[state.currentArrayIdx] ?? null;
}

/** Helper: trova `array_idx` dalla viewbox.index numerica (cercando matchante).
 *  Necessario perche' il backend espone `default_viewbox_index` come VALORE
 *  numerico (es. 2) mentre il client lavora con posizione nell'array (es. 0,
 *  1, 2). Se il numero non e' tra le viewbox available → null (caller usa
 *  fallback al primo available). */
export function arrayIdxFromNumericIndex(
	available: readonly ModelViewbox[],
	numericIdx: number,
): number | null {
	for (let i = 0; i < available.length; i++) {
		if (available[i]?.index === numericIdx) return i;
	}
	return null;
}

/** Re-export per chiarezza: il modulo `viewbox/*` si appoggia sui tipi
 *  centrali in api/types. */
export type { ModelViewbox, Vec3 };
